import { add, parseISO } from 'date-fns';
import { FV, FVAnnuityDue, geometricMean } from '../financeMath';
import { getRecurrenceConfig, TransactionSchedulePlan } from '../types';
import { sum } from '../util/arrays';
import { dt } from '../util/dateTime';
import { getNextRecurrence } from '../util/recurrence';
import { duration, getUnitsPerYear, toDateStr } from '../valueSeries/ticks';
import { getNumPeriods } from './helpers';

export interface CalculateForecastAmountInput {
    startAmount: number;
    transactionSchedulePlans?: TransactionSchedulePlan[];
    annualGrowthRate: number;
    startDate: string;
    targetDate: string;
}

export const calculateForecastAmount = ({
    startAmount,
    transactionSchedulePlans,
    annualGrowthRate,
    startDate,
    targetDate,
}: CalculateForecastAmountInput): number => {
    const startingBalance = startAmount;
    const daysToTarget = getNumPeriods({ startDate, targetDate, unit: 'day' });
    const dailyInterestRate = geometricMean(
        annualGrowthRate,
        getUnitsPerYear('day')
    );

    // First, calculate future value of starting balance on target date
    const fvStartingBalance = Math.round(
        FV({
            startingBalance,
            interestRate: dailyInterestRate,
            numPeriods: daysToTarget,
        })
    );

    // If no transaction schedule, or the next transaction schedule deposit
    // would occur *after* target date, just return FV of starting balance.

    if (!transactionSchedulePlans || transactionSchedulePlans.length === 0) {
        return fvStartingBalance;
    }

    const fvRecurringDeposits = sum(
        transactionSchedulePlans,
        (transactionSchedulePlan) =>
            calculateTransactionSchedulePlanForecastAmount({
                transactionSchedulePlan,
                annualGrowthRate,
                startDate,
                targetDate,
                dailyInterestRate,
            })
    );

    return Math.round(fvRecurringDeposits + fvStartingBalance);
};

const calculateTransactionSchedulePlanForecastAmount = ({
    transactionSchedulePlan,
    annualGrowthRate,
    dailyInterestRate,
    startDate,
    targetDate,
}: {
    transactionSchedulePlan: TransactionSchedulePlan;
    annualGrowthRate: number;
    dailyInterestRate: number;
    startDate: string;
    targetDate: string;
}): number => {
    const recurrenceStartDate = getNextRecurrence({
        dtStart: dt(transactionSchedulePlan.startDate),
        recurrence: transactionSchedulePlan.recurrence,
        dtOnOrAfter: dt(startDate),
    }).toDateStr();

    if (recurrenceStartDate > targetDate) return 0;

    // Second, calculate future value of recurring deposits from transaction
    // schedule plan

    const { unit, perYear } = getRecurrenceConfig(
        transactionSchedulePlan.recurrence
    );

    const numPeriods = getNumPeriods({
        startDate: recurrenceStartDate,
        targetDate,
        unit,
        interval: transactionSchedulePlan.recurrence.interval,
    });
    // Add one period to represent the "immediate" deposit that occurs at
    // the recurrence start.
    const numDeposits = numPeriods + 1;

    const periodInterestRate = geometricMean(annualGrowthRate, perYear);
    let fvRecurringDeposits = FVAnnuityDue({
        payment: transactionSchedulePlan.amount,
        interestRate: periodInterestRate,
        numPeriods: numDeposits,
    });

    // Lastly, if final recurring deposit ended before target date,
    // calculate FV of recurring deposits from then until target date.

    const lastDepositDate = toDateStr(
        add(
            parseISO(recurrenceStartDate),
            duration(
                numPeriods * transactionSchedulePlan.recurrence.interval,
                unit
            )
        )
    );
    const daysToTargetFromLastDeposit = getNumPeriods({
        startDate: lastDepositDate,
        targetDate,
        unit: 'day',
    });
    if (daysToTargetFromLastDeposit) {
        fvRecurringDeposits = FV({
            startingBalance: fvRecurringDeposits,
            interestRate: dailyInterestRate,
            numPeriods: daysToTargetFromLastDeposit,
        });
    }

    return fvRecurringDeposits;
};
